import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../../lib/i18n/LanguageContext';
import { useLocalizedName, useLocalizedDescription } from '../../lib/utils/formatters';
import { fetchMenuItems, fetchCategories } from '../../lib/supabase/api';
import type { MenuItemWithCategory, Category } from '../../lib/supabase/types';
import { usePageNavigation } from '../../lib/contexts/PageNavigationContext';
import SEO from '../../components/SEO';

// Import modular components
import MenuLoadingError from './MenuLoadingError';
import MenuContent from './MenuContent';
import CategorySubNav from '../../components/ui/CategorySubNav';

const Menu = () => {
  const { t } = useLanguage();
  const location = useLocation();
  const getLocalizedName = useLocalizedName();
  const getLocalizedDescription = useLocalizedDescription();
  const [categories, setCategories] = useState<Category[]>([]);
  const [menuItems, setMenuItems] = useState<MenuItemWithCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { setCurrentPageType, setCategories: setPageCategories } = usePageNavigation();

  // Create stable slugs for categories
  const categoriesWithSlugs = categories.map(category => ({
    ...category,
    slug: category.name.toLowerCase().replace(/\s+/g, '-'),
  }));

  // Group menu items by category
  const groupedMenuItems = categoriesWithSlugs.reduce(
    (acc, category) => {
      acc[category.slug] = menuItems.filter(item => item.category_id === category.id);
      return acc;
    },
    {} as Record<string, MenuItemWithCategory[]>
  );

  useEffect(() => {
    window.scrollTo(0, 0);

    // Set page type for navigation context
    setCurrentPageType('menu');

    // Cleanup when leaving the page
    return () => {
      setCurrentPageType(null);
      setPageCategories([]);
    };
  }, [t, setCurrentPageType, setPageCategories]);

  // Handle scroll to category from header navigation
  useEffect(() => {
    const targetCategorySlug = (location.state as any)?.categorySlug;
    if (targetCategorySlug && categoriesWithSlugs.some(cat => cat.slug === targetCategorySlug)) {
      // Scroll to the category after a brief delay to ensure content is loaded
      setTimeout(() => {
        const element = document.getElementById(targetCategorySlug);
        if (element) {
          const elementTop = element.offsetTop - 140 - 20;
          window.scrollTo({
            top: Math.max(0, elementTop),
            behavior: 'smooth',
          });
        }
      }, 100);
    }
  }, [location.state, categoriesWithSlugs]);

  // Load menu data
  useEffect(() => {
    const loadMenuData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const [categoriesData, menuItemsData] = await Promise.all([
          fetchCategories(),
          fetchMenuItems(),
        ]);

        setCategories(categoriesData);
        setMenuItems(menuItemsData);

        // Update page navigation context with categories
        setPageCategories(categoriesData);
      } catch (err) {
        console.error('Failed to load menu data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load menu data');
      } finally {
        setIsLoading(false);
      }
    };

    loadMenuData();
  }, [setPageCategories]);

  if (isLoading || error) {
    return <MenuLoadingError isLoading={isLoading} error={error} t={t} />;
  }

  // Structured data for menu page
  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'Menu',
      name: t.menu.hero.title,
      description: t.menu.hero.subtitle,
      hasMenuSection: categoriesWithSlugs.map(category => ({
        '@type': 'MenuSection',
        name: getLocalizedName(category),
        hasMenuItem: (groupedMenuItems[category.slug] || []).map(item => ({
          '@type': 'MenuItem',
          name: getLocalizedName(item),
          description: getLocalizedDescription(item),
          offers: {
            '@type': 'Offer',
            price: item.price,
            priceCurrency: 'ISK',
          },
        })),
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://rossopomodoro.is/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Menu',
          item: 'https://rossopomodoro.is/menu',
        },
      ],
    },
  ];
  return (
    <>
      <SEO
        title={`${t.menu.hero.title} | Rossopomodoro`}
        description="Explore our authentic Italian menu featuring Neapolitan pizza, fresh pasta & traditional dishes. Best Italian food in Reykjavik made with imported ingredients. Wood-fired oven."
        canonical="https://rossopomodoro.is/menu"
        keywords="Italian menu Reykjavik, Neapolitan pizza menu, authentic pasta dishes, wood-fired pizza, Italian food Reykjavik, traditional Italian recipes, imported ingredients"
        ldjson={structuredData}
      />
      <MenuContent categoriesWithSlugs={categoriesWithSlugs} groupedMenuItems={groupedMenuItems} />
      {/* <MenuKey /> */}
      <CategorySubNav />
    </>
  );
};

export default Menu;
